import React from 'react';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Popconfirm,
  Row,
  Space,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { usePW2501Query } from './hooks/usePW2501Query';
import QueryParams from './components/QueryParams';
import ApprovalTable from './components/ApprovalTable';
import './PW2501Form.css';
import { pw2501Api } from '../../services/pw2501Api';
import { BatchAcceptanceBridge, pw2501f1AcceptanceManifest } from '../../features/acceptance';

const { Title, Text } = Typography;

const PW2501Form: React.FC = () => {
  const {
    loading,
    approving,
    pendingItems,
    selectedKeys,
    filter,
    postToInventory,
    setFilter,
    setSelectedKeys,
    setPostToInventory,
    fetchPending,
    batchApprove,
    batchUnapprove,
  } = usePW2501Query();

  // Auto-load on mount
  React.useEffect(() => {
    fetchPending();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="pw2501-container">
      <Title level={4} style={{ marginBottom: 16 }}>
        採購入庫單-核准 (pw2501f1)
      </Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <QueryParams
          filter={filter}
          loading={loading}
          onFilterChange={setFilter}
          onSearch={fetchPending}
        />
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{pendingItems.length}</strong> 筆待核准
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Space>
              <Checkbox
                checked={postToInventory}
                onChange={(e) => setPostToInventory(e.target.checked)}
              >
                同時過帳入庫
              </Checkbox>
              <Popconfirm
                title={`確認核准 ${selectedKeys.length} 筆入庫單？${postToInventory ? '（含過帳入庫）' : ''}`}
                onConfirm={batchApprove}
                okText="確認核准"
                cancelText="取消"
                disabled={selectedKeys.length === 0}
              >
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={approving}
                  disabled={selectedKeys.length === 0}
                >
                  批量核准
                </Button>
              </Popconfirm>
              <Popconfirm
                title={`確認取消核准 ${selectedKeys.length} 筆入庫單？`}
                onConfirm={batchUnapprove}
                okText="確認取消"
                cancelText="返回"
                disabled={selectedKeys.length === 0}
              >
                <Button
                  icon={<RollbackOutlined />}
                  loading={approving}
                  disabled={selectedKeys.length === 0}
                  danger
                >
                  取消核准
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <ApprovalTable
        data={pendingItems}
        loading={loading}
        selectedKeys={selectedKeys}
        onSelectedChange={setSelectedKeys}
      />
      <BatchAcceptanceBridge
        manifest={pw2501f1AcceptanceManifest}
        records={pendingItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.f2110_01}
        queryExact={async (key) => {
          const response = await pw2501Api.query(filter);
          return response.data.find((item) => item.f2110_01 === key) || null;
        }}
        lastApiResponse={pendingItems}
        loading={loading || approving}
        onAutoFill={() => pendingItems[0] && setSelectedKeys([pendingItems[0].f2110_01])}
        autoFillDisabled={pendingItems.length === 0}
        screenDraft={{ filter, postToInventory }}
      />
    </div>
  );
};

export default PW2501Form;
